/**
 * Whether a guardrail's conditions read the AI context in a way an agent's call can satisfy, and
 * the leaf that says so.
 *
 * Two consumers, which is why this is its own module rather than a private helper in either:
 * the rule dialog (such a rule can only ever be evaluated for an agent call, and an agent has no
 * channel to acknowledge a warning, so it must block) and the claim-action section (the same
 * reference is what identifies a rule as that action's).
 *
 * Mirrors `_references_an_agent_condition` in
 * `server/modules/guardrails/internal/managers/guardrails_config_manager.py`, including its
 * `Is AI Agent` exemption — a FE that flagged a rule the BE exempts would withdraw an outcome the
 * BE accepts, and list a human's rule under the agent's action.
 */

import type { RuleGroupType, RuleType } from 'react-querybuilder';

/** Prefix of every condition field the AI context provider contributes (`ai.confidence_score`,
 *  `ai.is_ai_agent`). Must match `ai_context_provider/interface.py::AI_CONTEXT_KEY`. */
export const AI_FIELD_PREFIX = 'ai.';

/**
 * Seed leaf for a guardrail authored against a claim action. `ai.is_ai_agent is true` is not a
 * convenience default: without it the rule would trigger on the same domain event an *adjuster*
 * fires, so a guardrail meant for the agent would also gate every manual action on that event.
 * It doubles as the marker that identifies the rule as this action's on the way back out.
 */
export const AI_AGENT_CONDITION_FIELD = 'ai.is_ai_agent';

const isRuleGroup = (node: RuleGroupType | RuleType): node is RuleGroupType =>
  Array.isArray((node as RuleGroupType).rules);

/** Depth-first walk of a react-querybuilder tree, yielding only its leaves. */
export const flattenLeaves = (node?: RuleGroupType | RuleType | null): RuleType[] => {
  if (!node) {
    return [];
  }
  if (!isRuleGroup(node)) {
    return [node];
  }
  return (node.rules ?? []).flatMap((child) => flattenLeaves(child as RuleGroupType | RuleType));
};

/** The true/false spellings the CEL compiler reads for a boolean field. These are the sets
 *  `coerce_scalar` in `server/modules/rules_engine/cel/value_coercion.py` matches on, which is what
 *  the BE's own exemption reads the value through — a tree can arrive here straight from the API, so
 *  the persisted spelling is whatever an earlier client sent, not just what this editor produces. */
const TRUE_SPELLINGS = new Set(['true', '1', 'yes']);
const FALSE_SPELLINGS = new Set(['false', '0', 'no']);

/** `value` read the way the CEL compiler will read it for a boolean field, or `null` when it is
 *  not a boolean the compiler can read either (the BE raises its own 400 a step later).
 *
 *  Agrees with `coerce_scalar` on every scalar: the spellings above, booleans, numbers and
 *  null/undefined. It is deliberately stricter for a *non-scalar* — `coerce_scalar([], boolean)` is
 *  `'false'` by Python truthiness, where this returns `null` — so a leaf carrying `[]` is not read
 *  as the human-only exemption here. That is the safe direction to diverge in: the FE offers Block
 *  where the BE would have allowed Warn, never the reverse. */
const asCompilerBoolean = (value: unknown): 'true' | 'false' | null => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (TRUE_SPELLINGS.has(normalized)) {
      return 'true';
    }
    return FALSE_SPELLINGS.has(normalized) ? 'false' : null;
  }
  if (typeof value === 'boolean' || typeof value === 'number' || value === null || value === undefined) {
    return value ? 'true' : 'false';
  }
  return null;
};

/**
 * Whether this leaf is the `Is AI Agent` test that no agent's call can satisfy.
 *
 * Both spellings the boolean field editor offers, because they compile to the same predicate and
 * an admin picking either means the same rule: `BOOLEAN_OPS` offers `=` and `!=`, so "is not
 * checked" arrives as `= false` and "is not" + checked as `!= true`. Such a rule matches a
 * *person*, who has the interactive channel a warning needs — so it neither forces Block nor
 * marks the rule as the agent's.
 */
const leafMatchesOnlyAPerson = (leaf: RuleType): boolean => {
  if (leaf.field !== AI_AGENT_CONDITION_FIELD) {
    return false;
  }
  const value = asCompilerBoolean(leaf.value);
  return (leaf.operator === '=' && value === 'false') || (leaf.operator === '!=' && value === 'true');
};

/** Whether any leaf of the tree — at any depth — reads a field the AI context provider serves in a
 *  way an agent's call can satisfy. Per-leaf, like the BE: one such leaf is enough, and a
 *  human-only `Is AI Agent` leaf beside it does not soften it. */
export const hasAiConditionReference = (tree?: RuleGroupType | null): boolean =>
  flattenLeaves(tree).some((leaf) => (leaf.field ?? '').startsWith(AI_FIELD_PREFIX) && !leafMatchesOnlyAPerson(leaf));
